import React, { useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import { Button, Nav } from 'react-bootstrap';
import {useDispatch, useSelector} from "react-redux";
import {useAppInsightsContext} from "@microsoft/applicationinsights-react-js";
import {BrowserView, MobileView} from "react-device-detect";
import { useNavigate } from "react-router-dom";
import {RootState} from "app/store";

// import {useGetUserRewardsMutation} from "app/services/api/mutateGateAPI";
import {updateBalance, updateUserDetail} from "app/store/slices/userSlice";
import {useGetUserDetailsMutation, useGetUserRewardsMutation} from "app/services/api/mutateGateAPI";
import logging from "app/shared/utils/logging";
import {RewardsAccountResponse, RewardsAccountType} from "app/features/Categories/types/RewardsAccountType";
import {UserDetailResponse, UserDetailResponseType} from "app/features/Login/types/UserDetailResponseType";
import {useGetAllRewardsQuery} from "app/services/api/fetchGateApi";
import {processRewards} from "app/shared/utils/helperFunctions";
import {saveApplicableRewards} from "app/store/slices/rewardsSlice";
import {updateShowChangePickUpDetailsModal} from "app/store/slices/storeSlice";
import {RewardsDetails} from "app/features/Rewards/types/RewardsDetails";
import {decrypt} from "app/shared/utils/EncryptionUtility";
import {setHeaderServiceFailure, setSelectedMyAccountSelection, toggleAuthModal} from "app/store/slices/authSlice";
import Constants, { MyAccountMenu } from "app/shared/constants/constants";
import useAppUtil from "./useAppUtil";
import EllipsisText from './EllipsisText';
import PickupScheduleButton from './PickupScheduleButton';

const USERNAME_CHAR_LIMIT = 8;

interface Props {
    displayMode?: string,
    navbarExpanded?: boolean,
    onHide?: () => void,
}

const UserProfileView: React.FC<Props> = ({ displayMode = "", navbarExpanded = false, onHide = () => {}}) => {
    const { logoutHandler } = useAppUtil();
    const appInsights = useAppInsightsContext();
    const navigate = useNavigate();
    const {
        balanceAmount, balancePoints, firstName, cardNumber,
    } = useSelector((state: RootState) => state.userInfo);
    const {user, isGuest} = useSelector((state: RootState) => state.auth);
    const storeDescription = useSelector((state: RootState) => state.stores.storeDescription);
    const { pickUpType, pickUpTime} = useSelector((state: RootState) => state.stores);
    const dispatch = useDispatch();
    const {rewards} = useSelector((state: RootState) => state.userInfo);
    const [getUserRewards, {error: getUserRewardsError}] = useGetUserRewardsMutation();
    const [getUserDetails, {error: getUserDetailsError}] = useGetUserDetailsMutation();
    const {data} = useGetAllRewardsQuery('', {skip: isGuest});
    const {modalOpen} = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        if (data && rewards) {
            const tempValue: string = decrypt(data?.value?.toString());
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const tmpData: RewardsDetails[] = JSON.parse(tempValue);
            const applicableRewards = processRewards(rewards, tmpData);
            dispatch(saveApplicableRewards(applicableRewards));
        }
        // eslint-disable-next-line
    }, [data, rewards]);

    useEffect(() => {
        // eslint-disable-next-line
        (async () => {
            if (user && !isGuest) {
                try {
                    const response: UserDetailResponse = await getUserDetails({uid: user}).unwrap();
                    const tempValue: string = decrypt(response.value.toString());
                    // eslint-disable-next-line
                    const userDetails: UserDetailResponseType = JSON.parse(tempValue)
                    dispatch(updateUserDetail(userDetails));
                } catch (e) {
                    logging.error(appInsights, "_USER_PROF_UEFF", "Error getting user details", e);
                }
            }
        })();
        // eslint-disable-next-line
    }, [user]);

    useEffect(() => {
        // eslint-disable-next-line
        (async () => {
            if (!isGuest && user && cardNumber) {
                try {
                    // eslint-disable-next-line max-len
                    const response: RewardsAccountResponse = await getUserRewards({user, cardNumber}).unwrap();
                    const tempValue: string = decrypt(response.value.toString());
                    // eslint-disable-next-line
                    const pointsData: RewardsAccountType = JSON.parse(tempValue);
                    dispatch(updateBalance(pointsData));
                } catch (e) {
                    logging.error(appInsights, "_USER_PROF_UEFF_2", "Error getting user details", e);
                }
            }
        })();
        // eslint-disable-next-line
    }, [cardNumber, user]);

    useEffect(() => {
        if (getUserRewardsError || getUserDetailsError) {
            logging.error(
                appInsights,
                "evt_UserProfileView_err",
                "Partial response from API call",
                (getUserRewardsError || getUserDetailsError),
            );
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            if (getUserDetailsError?.status === 400) {
                logoutHandler();
                dispatch(toggleAuthModal(true));
            } else {
                dispatch(setHeaderServiceFailure(true));
            }
        }
      // eslint-disable-next-line
    },[getUserRewardsError, getUserDetailsError, modalOpen])

    const renderMyAccountButton = () => (
        <Nav.Link
            eventKey="8"
            className="d-flex flex-row justify-content-start align-items-end p-0 "
            onClick={() => {
                dispatch(setSelectedMyAccountSelection(MyAccountMenu.PERSONAL_INFO));
                navigate("/myaccount");
            }}
        >
            <p className="sideMenuFont text-transform-none w-100">My Account</p>
        </Nav.Link>
    );

    return (
        <Container className="userCard">
            <BrowserView>
                <Container className={`d-flex m-0 p-0 ${displayMode === "column" ? "flex-column" : "flex-row"}`}>
                    <Container>
                        {user && !isGuest && firstName && (
                            <div className="ms-0 p-1 text-nowrap">
                                <div
                                    className="user-greeting profile-name-elipsis"
                                >
                                    <b className="text-wrap">
                                        Hello,
                                        {' '}
                                        <EllipsisText
                                            text={firstName}
                                            charLimit={USERNAME_CHAR_LIMIT}
                                        />
                                        !
                                    </b>
                                </div>
                                <p
                                    color="primary-light"
                                    className="user-info m-0"
                                >
                                    Points:
                                    {' '}
                                    {balancePoints}
                                </p>
                                <p
                                    className="user-info m-0"
                                >
                                    Balance:
                                    {' '}
                                    $
                                    {balanceAmount?.toFixed(2)}
                                </p>
                                {!isGuest && user
                                    && renderMyAccountButton()}
                                {navbarExpanded && (
                                    <div>&nbsp;&nbsp;&nbsp;</div>
                                )}
                            </div>
                        )}
                    </Container>
                    <Container>
                        <div className="d-flex flex-column justify-content-start brand-padding">
                            <Button
                                disabled={storeDescription === "" || storeDescription === Constants.NO_LOCATION_SELECTED}
                                type="button"
                                className="button-hover default-button text-start border-0 ml-0 m-0 pl-0 p-0 text-nowrap location-primary"
                                onClick={() => {
                                    navigate("/");
                                }}
                            >
                                <p className="user-info">
                                    {storeDescription}
                                    {' '}
                                </p>
                            </Button>
                            {(pickUpType !== "" || pickUpTime !== "") && (
                                <PickupScheduleButton
                                    className="button-hover text-start border-0 ml-0 m-0 pl-0 p-0 text-nowrap location-primary default-button"
                                    onClick={() => {
                                        dispatch(updateShowChangePickUpDetailsModal(true));
                                        onHide();
                                    }}
                                />
                            )}
                        </div>
                    </Container>
                </Container>
            </BrowserView>
            <MobileView>
                {user && !isGuest && (
                    <div className="ms-0 p-0 text-right">
                        <div
                            className="ms-1 user-greeting profile-name-elipsis"
                        >
                            <b className="text-wrap break-word">
                                Hello,
                                {' '}
                                <EllipsisText text={firstName} charLimit={USERNAME_CHAR_LIMIT} />
                                !
                            </b>
                        </div>
                        <p
                            color="primary-light"
                            className="user-info m-0 ms-1"
                        >
                            Points:
                            {' '}
                            {balancePoints}
                        </p>
                        <p className="user-info m-0 ms-1">
                            Balance:
                            {' '}
                            $
                            {balanceAmount?.toFixed(2)}
                        </p>
                    </div>
                )}
            </MobileView>
        </Container>
    );
};

export default UserProfileView;
